import React, { useCallback, useEffect, useId, useState } from 'react';
import { t } from '@lingui/macro';
import { z } from 'zod';

import { Box, Button, Heading, Paragraph, Text } from '@rover/kibble/core';
import { Form, useKibbleForm } from '@rover/kibble/forms';
import TextField from '@rover/kibble/forms/fields/TextField';
import Banner from '@rover/kibble/patterns/Banner';
import LocationInputContainer from '@rover/react-lib/src/containers/LocationInputContainer';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';

import SearchByNameSitterCard from '../components/SearchByNameSitterCard';
import { MAX_NAME_LENGTH, MAX_RESULTS, MIN_NAME_LENGTH } from '../constants';
import type { NameSearchResult } from '../types';

const NAME_FIELD = 'sitterName';
const LOCATION_INPUT_ID = 'search-by-name-location';

type Props = {
  initialLocation?: string;
  results: NameSearchResult[] | null;
  query: string;
  isLoading: boolean;
  error: string | null;
  wasCapped: boolean;

  onSearch: (name: string, location: string) => void;
};

const ModalSearchScreen = ({
  initialLocation = '',
  results,
  query,
  isLoading,
  error,
  wasCapped,
  onSearch,
}: Props): JSX.Element => {
  const headingId = useId();
  const { setScreenReaderAnnouncement } = useA11yContext();
  const [location, setLocation] = useState(initialLocation);
  const [locationError, setLocationError] = useState<string | null>(null);

  const { configureField, formProps } = useKibbleForm({
    // Zod message is a sentinel; the user-facing error is rendered via translatedFieldErrorMessage below.
    schema: z.object({
      sitterName: z.string().trim().min(MIN_NAME_LENGTH, 'name-too-short'),
    }),
    defaultValues: { sitterName: '' },
    onValidSubmit: (data) => {
      if (isLoading) return;
      if (!location.trim()) {
        const message = t`Please enter a location`;
        setLocationError(message);
        setScreenReaderAnnouncement(message);
        // Kibble's LocationInputContainer doesn't forward a ref; focus by id.
        // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
        document.getElementById(LOCATION_INPUT_ID)?.focus();
        return;
      }
      // Clamp before passing through to the API + headings/SR announcements,
      // so a pathological query can't be interpolated verbatim into UI text.
      const clampedName = data.sitterName.slice(0, MAX_NAME_LENGTH);
      onSearch(clampedName, location);
    },
  });

  const nameFieldConfig = configureField(NAME_FIELD);
  const nameFieldErrorMessage = nameFieldConfig.fieldError
    ? t`Please enter at least ${MIN_NAME_LENGTH} characters of the sitter's name`
    : undefined;

  const handleLocationChange = useCallback((data: { value?: string | null } | null) => {
    const val = data?.value ?? '';
    setLocation(val);
    if (val.trim()) setLocationError(null);
  }, []);

  // Kibble TextField does not forward a ref; focus via the input's name attribute.
  useEffect(() => {
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    const input = document.querySelector<HTMLInputElement>(`input[name="${NAME_FIELD}"]`);
    input?.focus();
  }, []);

  // Focus the results heading on new results so SR + keyboard users land in the result region.
  useEffect(() => {
    if (results === null || isLoading || error) return;
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    const heading = document.getElementById(headingId);
    heading?.focus();
  }, [results, isLoading, error, headingId]);

  const hasSearched = results !== null;
  const hasResults = (results?.length ?? 0) > 0;

  return (
    <Box testID="search-by-name-screen">
      <Box p="4x">
        <Form {...formProps} hideRequiredFieldWarning>
          <Box mb="4x">
            <TextField
              {...nameFieldConfig}
              translatedFieldErrorMessage={nameFieldErrorMessage}
              primaryLabel={t`Sitter name`}
              placeholder={t`Enter sitter's name`}
              hideRequiredAsterisk
            />
          </Box>
          <Box mb="6x">
            <Text
              as="label"
              fontWeight="semibold"
              mb="2x"
              display="block"
              webProps={{ htmlFor: LOCATION_INPUT_ID }}
            >
              {t`Location`}
            </Text>
            <LocationInputContainer
              inputId={LOCATION_INPUT_ID}
              inputValue={location}
              onChange={handleLocationChange}
              placeholder={t`City, state, or zip code`}
              validationType="inline"
              validationError={locationError}
              channel="search-by-name"
            />
          </Box>
          <Button
            variant="primary"
            fullWidth
            webProps={{ type: 'submit' }}
            loading={isLoading}
            disabled={isLoading}
            testID="search-by-name-submit"
          >
            {t`Search`}
          </Button>
        </Form>
      </Box>

      {error && (
        <Box px="4x" pb="2x">
          <Banner severity="danger" testID="search-by-name-error">
            {error}
          </Banner>
        </Box>
      )}

      {hasSearched && !error && (
        <>
          <Box px="4x" pb="2x">
            <Banner severity="info" testID="search-by-name-privacy-banner">
              {t`For sitter safety, location details are hidden in these results.`}
            </Banner>
          </Box>

          {wasCapped && hasResults && (
            <Box px="4x" pb="2x" testID="search-by-name-cap-notice">
              <Banner severity="info" title={t`Showing the top ${MAX_RESULTS} matches`}>
                {t`Add a last name or a more specific location to narrow your results.`}
              </Banner>
            </Box>
          )}

          {/* Heading's webProps doesn't accept tabIndex; hang the focus target on the wrapping Box.
              aria-labelledby still resolves to the heading text via the Box's accessible name. */}
          <Box px="4x" pb="2x" webProps={{ id: headingId, tabIndex: -1 }}>
            <Heading size="300" testID="search-by-name-results-heading">
              {query ? t`Results for ${query}` : t`Results`}
            </Heading>
          </Box>

          <Box px="4x">
            {!hasResults ? (
              <Box
                py="8x"
                textAlign="center"
                testID="search-by-name-empty-results"
                webAs="section"
                webProps={{ 'aria-labelledby': headingId }}
              >
                <Paragraph size="200" textColor="secondary">
                  {t`No sitters found matching ${query}.`}
                </Paragraph>
                <Text size="100" textColor="tertiary" mt="2x">
                  {t`Try a different spelling or location.`}
                </Text>
              </Box>
            ) : (
              <Box
                webAs="ul"
                webProps={{ 'aria-labelledby': headingId }}
                p="0x"
                m="0x"
                sx={{ listStyle: 'none' }}
              >
                {results?.map((result) => (
                  <Box webAs="li" key={result.personOpk}>
                    <SearchByNameSitterCard result={result} />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ModalSearchScreen;
